import { AddOrEditMassTortCustomFieldModal } from "@/components/features/mass-torts/AddOrEditMassTortCustomFieldModal";
import {
  Button,
  Input,
  LoadingSpinner,
  Text,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useUpdateMassTortCustomFields } from "@/lib/data/react-query/mass-torts/mass-tort";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MassTortCustomField } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ErrorBoundary } from "@sentry/react";
import { GripVertical, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

type MassTortCustomFieldRow = MassTortCustomField & {
  originalIndex: number;
  rowId: string;
};

type MassTortCustomFieldsListViewProps = {
  massTortId: string;
  massTortCustomFields: MassTortCustomField[];
};

const getOrderedCustomFields = (customFields: MassTortCustomField[]) =>
  customFields
    .map((customField, originalIndex) => ({
      ...customField,
      originalIndex,
    }))
    .sort((a, b) => a.order - b.order || a.originalIndex - b.originalIndex);

const normalizeCustomFieldOrder = (
  customFields: Array<
    MassTortCustomField & { originalIndex?: number; rowId?: string }
  >,
) =>
  customFields.map((customField, index) => ({
    dateModified: customField.dateModified,
    id: customField.id,
    order: index,
  }));

const getValueTypeLabel = (valueType: MassTortCustomField["valueType"]) => {
  if (valueType === "select") return "Select";
  if (valueType === "text") return "Text";
  return "Date";
};

function MassTortCustomFieldsListViewDangerous({
  massTortId,
  massTortCustomFields,
}: MassTortCustomFieldsListViewProps) {
  const { hasActionPermission } = useUser();
  const updateCustomFields = useUpdateMassTortCustomFields({
    successToast: null,
  });
  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortCustomField,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTortCustomField,
    ACTION_PERMISSION_VALUES.update,
  );

  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeCustomFieldIndex, setActiveCustomFieldIndex] = useState<
    number | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");

  const rows = useMemo<MassTortCustomFieldRow[]>(
    () =>
      getOrderedCustomFields(massTortCustomFields).map((customField) => ({
        ...customField,
        rowId: customField.id,
      })),
    [massTortCustomFields],
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const searchableText = [
        row.label,
        row.description,
        row.order,
        row.valueType,
        row.valueOptions?.join(" "),
      ]
        .filter((value) => value != null)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [rows, searchQuery]);

  const rowIds = useMemo(
    () => filteredRows.map((customField) => customField.rowId),
    [filteredRows],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (!canUpdate || updateCustomFields.isPending) {
        return;
      }

      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = rows.findIndex((row) => row.rowId === active.id);
      const newIndex = rows.findIndex((row) => row.rowId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      const reordered = arrayMove(rows, oldIndex, newIndex);
      await updateCustomFields.mutateAsync({
        massTortId,
        customFields: normalizeCustomFieldOrder(reordered),
      });
    },
    [canUpdate, massTortId, rows, updateCustomFields],
  );

  const activeCustomField =
    activeCustomFieldIndex == null
      ? undefined
      : massTortCustomFields[activeCustomFieldIndex];

  return (
    <>
      <div className="rounded-md border">
        <div className="border-border bg-muted/30 flex min-w-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-[220px] flex-1">
            <Input
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
              }}
              placeholder="Search custom fields..."
              className="bg-background max-w-sm"
            />
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {canCreate ? (
              <Button
                variant="foreground"
                size="md"
                className="shrink-0"
                onClick={() => {
                  setIsAdding(true);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Create custom field
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between border-b px-4 py-2 text-sm font-medium">
          <span>Custom fields</span>
          <span className="text-muted-foreground">
            {filteredRows.length}{" "}
            {filteredRows.length === 1 ? "custom field" : "custom fields"}
          </span>
        </div>

        {filteredRows.length > 0 ? (
          <div
            className={cn(
              "relative divide-y",
              updateCustomFields.isPending && "pointer-events-none opacity-70",
            )}
          >
            {updateCustomFields.isPending ? (
              <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : null}
            <DndContext
              collisionDetection={closestCenter}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rowIds}
                strategy={verticalListSortingStrategy}
              >
                {filteredRows.map((customField) => {
                  const orderedIndex = rows.findIndex(
                    (row) => row.rowId === customField.rowId,
                  );
                  return (
                    <MassTortCustomFieldListItem
                      key={customField.rowId}
                      customField={customField}
                      index={orderedIndex}
                      canUpdate={canUpdate}
                      isBusy={updateCustomFields.isPending}
                      onOpen={() => {
                        setActiveCustomFieldIndex(customField.originalIndex);
                        setIsEditing(true);
                      }}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        ) : (
          <div className="text-muted-foreground px-4 py-8 text-center text-sm">
            No custom fields found.
          </div>
        )}
      </div>

      <AddOrEditMassTortCustomFieldModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
        massTortId={massTortId}
        massTortCustomFields={massTortCustomFields}
      />

      {activeCustomFieldIndex != null && activeCustomField ? (
        <AddOrEditMassTortCustomFieldModal
          editing
          open={isEditing}
          setOpen={setIsEditing}
          massTortId={massTortId}
          massTortCustomFields={massTortCustomFields}
          activeCustomField={activeCustomField}
          activeCustomFieldIndex={activeCustomFieldIndex}
          isReadOnly={!canUpdate}
        />
      ) : null}
    </>
  );
}

type MassTortCustomFieldListItemProps = {
  customField: MassTortCustomFieldRow;
  index: number;
  canUpdate: boolean;
  isBusy: boolean;
  onOpen: () => void;
};

function MassTortCustomFieldListItem({
  customField,
  index,
  canUpdate,
  isBusy,
  onOpen,
}: MassTortCustomFieldListItemProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: customField.rowId,
    disabled: !canUpdate || isBusy,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const valueOptions = customField.valueOptions?.filter(Boolean) ?? [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-card flex cursor-pointer flex-col gap-3 px-4 py-3",
        "hover:bg-primary/5",
        isDragging && "relative z-10 shadow-sm",
      )}
      onClick={onOpen}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-muted-foreground w-5 text-sm font-semibold">
          {index + 1}
        </div>
        {canUpdate ? (
          <button
            type="button"
            className={cn(
              "text-muted-foreground flex h-7 w-7 items-center justify-center rounded-md border",
              isBusy
                ? "cursor-not-allowed opacity-50"
                : "bg-background hover:bg-muted cursor-grab",
            )}
            aria-label="Reorder custom field"
            disabled={isBusy}
            onClick={(event) => {
              event.stopPropagation();
            }}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <Typography weight="medium" className="truncate">
            {customField.label || "-"}
          </Typography>
        </div>
        <div className="text-muted-foreground rounded-full border px-2 py-0.5 text-xs">
          {getValueTypeLabel(customField.valueType)}
        </div>
      </div>

      <div className="text-muted-foreground ml-8 grid gap-2 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <span className="text-foreground font-medium">Description: </span>
          {customField.description || "-"}
        </div>
        <div className="min-w-0">
          <span className="text-foreground font-medium">Value options: </span>
          {customField.valueType !== "select"
            ? "N/A"
            : valueOptions.length > 0
              ? valueOptions.join(", ")
              : "-"}
        </div>
      </div>
    </div>
  );
}

export function MassTortCustomFieldsListView(
  props: MassTortCustomFieldsListViewProps,
) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MassTortCustomFieldsListViewDangerous {...props} />
    </ErrorBoundary>
  );
}
